import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { ClaimNotes, STATUS_STYLE } from "@/components/claims/ClaimCard";
import { ClaimVideos } from "@/components/ClaimVideos";
import { IssuePhotos } from "@/components/IssuePhotos";
import { Badge } from "@/components/ui/badge";

const VIEWERS = new Set(["back_office", "super_admin", "factory_1"]);

// Light-red strip at the top of an order with a claim (the original order AND the
// PO-xxxx-W / -R replacement): defect note + solution, both translated.
export function ClaimBanner({ order, user }) {
  const { t, lang } = useT();
  const relevant = !!(order?.last_claim_id || order?.claim_id || order?.claims_count);
  const { data } = useQuery({
    queryKey: ["claims", "order", order?.id],
    queryFn: async () => (await api.get(`/claims?order_id=${order.id}`)).data,
    enabled: relevant && VIEWERS.has(user?.role),
  });
  const claims = data?.claims || [];
  if (!relevant || !VIEWERS.has(user?.role) || claims.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="claim-banner">
      {claims.map((c) => {
        const isReplacement = c.replacement_order_id === order.id;
        return (
          <div key={c.id} className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm" data-testid={`claim-banner-${c.id}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="font-bold text-red-900">⚠️ {isReplacement ? t("Claim replacement of") : t("After-sale claim")}</span>
              {isReplacement && <Link to={`/order/${c.order_id}`} className="font-mono font-bold underline text-red-900">{c.po_number}</Link>}
              <Badge className={`border font-bold uppercase tracking-wider text-[10px] ${STATUS_STYLE[c.status] || ""}`} data-testid={`claim-banner-status-${c.id}`}>
                {c.status === "open" ? t("OPEN · waiting for Factory One / Super Admin") : t(c.decision_label || c.status)}
              </Badge>
              {c.replacement_po && !isReplacement && <Link to={`/order/${c.replacement_order_id}`} className="font-mono text-xs underline text-emerald-900">→ {c.replacement_po}</Link>}
              {c.return_requested && <span className="text-xs text-sky-800">↩ {t("old set to be returned")}{c.return_label ? ` · ✓ ${t("label")}` : ""}</span>}
              <Link to={`/claims?claim=${c.id}`} className="ml-auto text-xs underline text-red-800" data-testid={`claim-banner-link-${c.id}`}>{t("Open Claims")} ↗</Link>
            </div>
            <ClaimNotes c={c} lang={lang} t={t} testid={`claim-banner-${c.id}`} />
            <IssuePhotos photos={c.photo_ids || []} size="h-16" testid={`claim-banner-photos-${c.id}`} />
            <ClaimVideos videos={c.videos || []} ids={c.video_ids || []} compact testid={`claim-banner-videos-${c.id}`} />
          </div>
        );
      })}
    </div>
  );
}
